import { type ClassValue, clsx } from 'clsx'
import {
	format,
	isBefore,
	isToday,
	parseISO,
	startOfToday,
	subYears
} from 'date-fns'
import { it } from 'date-fns/locale'
import { twMerge } from 'tailwind-merge'

import { POST_TYPE, ROLES } from '@/helpers/constants'

import type { UserMission } from '@/models/api'
import type { RoleKey } from '@/models/auth'
import type { PostType } from '@/models/blog'

function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs))
}

function formatDate(date: string): string {
	return format(date, 'dd/MM/yyyy', { locale: it })
}

function formatDatetime(date: string): string {
	const iso = parseISO(date)
	if (isToday(iso)) return `Oggi, ${format(iso, 'HH:mm')}`
	return format(iso, 'd MMMM yyyy, HH:mm', { locale: it })
}

function formatTimestamp(timestamp: number): string {
	return format(new Date(timestamp), 'yyyy/MM/dd HH:mm:ss.SSS', { locale: it })
}

function formatTimestampToDatetimeLocal(timestamp: string): string {
	return format(new Date(Number(timestamp)), "yyyy-MM-dd'T'HH:mm")
}

const getUserInitials = (name: string | undefined): string | undefined => {
	if (!name) return undefined
	const [firstName, lastName] = name.split(' ')
	if (!lastName) return firstName[0]
	return `${firstName[0]}${lastName[0]}`
}

const isAdult = (birthDate: string): boolean => {
	const today = startOfToday()
	const dataMaggiorenne = subYears(today, 18)

	return isBefore(birthDate, dataMaggiorenne)
}

const isAuthorizedUser = (
	authorizedRoles: RoleKey[],
	userRoles: RoleKey[]
): boolean => {
	const validAuthorized = authorizedRoles.filter(role =>
		Object.keys(ROLES).includes(role)
	)
	if (validAuthorized.length === 0) {
		return true
	}
	return validAuthorized.some(role => userRoles?.includes(role))
}

const isValidPostType = (postType: PostType): boolean => {
	return Object.keys(POST_TYPE)
		.map(type => type)
		.includes(postType)
}

function getSortedMissions(missions: UserMission[], { desc = true }) {
	return missions?.toSorted((a, b) => {
		const aDate = a.userCoupons?.[0]?.used_at
			? new Date(a.userCoupons[0].used_at).getTime()
			: 0
		const bDate = b.userCoupons?.[0]?.used_at
			? new Date(b.userCoupons[0].used_at).getTime()
			: 0
		return desc ? bDate - aDate : aDate - bDate
	})
}

function getFormattedMissionsDate(missions: UserMission[]) {
	return missions
		.filter(item => item.value > 0 || item.mission_type === 'customer')
		.map(item => ({
			...item,
			last_product_update: item.userCoupons?.[0]?.used_at
				? format(new Date(item.userCoupons[0].used_at), 'dd/MM/yyyy')
				: null
		}))
}

export {
	cn,
	formatDate,
	formatDatetime,
	formatTimestamp,
	formatTimestampToDatetimeLocal,
	getFormattedMissionsDate,
	getSortedMissions,
	getUserInitials,
	isAdult,
	isAuthorizedUser,
	isValidPostType
}
